// Imports
import ___CSS_LOADER_API_SOURCEMAP_IMPORT___ from "../../../../node_modules/css-loader/dist/runtime/sourceMaps.js";
import ___CSS_LOADER_API_IMPORT___ from "../../../../node_modules/css-loader/dist/runtime/api.js";
var ___CSS_LOADER_EXPORT___ = ___CSS_LOADER_API_IMPORT___(___CSS_LOADER_API_SOURCEMAP_IMPORT___);
// Module
___CSS_LOADER_EXPORT___.push([module.id, `/* ==========================================================================
   Prediction — run a trained model against an image.

   Every colour here is a token from src/styles/theme.css. Nothing is
   hardcoded, because this screen has to be correct in both themes and the
   legacy sheet's \`h5 { color: white }\` makes a guess invisible on a light
   card (see DESIGN_GUIDE.md).
   ========================================================================== */

.lf-predict {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 0;
  /* Height comes from the shell (.lf-fill-viewport in responsive.css). This
     page used to subtract a guessed 100px, which left it 57px past the bottom
     of the screen. */
}

.lf-predict__header {
  flex: 0 0 auto;
  padding: 4px 2px 0;
}

.lf-predict__title {
  color: var(--foreground);
  font-size: 1.25rem;
  font-weight: 700;
  margin: 0;
}

.lf-predict__subtitle {
  color: var(--muted-foreground);
  font-size: 0.82rem;
  margin: 2px 0 0;
}

.lf-predict__grid {
  flex: 1 1 auto;
  min-height: 0;
  display: grid;
  grid-template-columns: minmax(240px, 1fr) minmax(320px, 1.7fr) minmax(240px, 1fr);
  /* The ROW has to be told to fill, not just the container. A grid row defaults
     to \`auto\`, which sizes to content — so the container stretched to the height
     the shell gave it and the three panels stopped wherever their contents ran
     out, leaving a band of bare page under them. Caught in a screenshot; no test
     can see it. \`minmax(0, 1fr)\` rather than \`1fr\` so a long model list scrolls
     inside its panel instead of pushing the grid taller than the viewport. */
  grid-template-rows: minmax(0, 1fr);
  gap: 10px;
}

/* ── panels ─────────────────────────────────────────────────────────────── */
.lf-predict-panel {
  display: flex;
  flex-direction: column;
  min-height: 0;
  /* \`1fr\` is \`minmax(auto, 1fr)\`, and \`auto\` floors at MIN-CONTENT — so one wide
     child (the image wrapper, a long model name) pushes the track past the
     viewport and the whole page scrolls sideways. Caught in a 375px screenshot:
     the confidence figures, the Run button and the meta values were all off the
     right edge. No test sees this. */
  min-width: 0;
  background: var(--card);
  border: 1px solid var(--border);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-sm);
  overflow: hidden;
}

.lf-predict-panel__head {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 10px 12px;
  border-bottom: 1px solid var(--border);
}

.lf-predict-panel__title {
  color: var(--foreground);
  font-size: 0.92rem;
  font-weight: 650;
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.lf-predict-panel__sub {
  color: var(--muted-foreground);
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  flex: 0 0 auto;
}

.lf-predict-panel__body {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.lf-predict-icon-btn {
  background: transparent;
  border: 1px solid var(--border);
  border-radius: var(--radius-sm);
  color: var(--muted-foreground);
  width: 26px;
  height: 26px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all var(--transition);
}

.lf-predict-icon-btn:hover {
  color: var(--foreground);
  border-color: var(--border-strong);
  background: var(--muted);
}

/* ── empty states ───────────────────────────────────────────────────────── */
.lf-predict-empty {
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  gap: 6px;
  padding: 24px 14px;
  color: var(--muted-foreground);
}

.lf-predict-empty i {
  font-size: 2rem;
  opacity: 0.7;
}

.lf-predict-empty__title {
  color: var(--foreground);
  font-size: 0.88rem;
  font-weight: 600;
  margin: 0;
}

.lf-predict-empty__hint {
  font-size: 0.78rem;
  margin: 0;
  max-width: 30ch;
}

/* ── search ─────────────────────────────────────────────────────────────── */
.lf-predict-search {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 10px 12px 0;
  padding: 6px 10px;
  border: 1px solid var(--border);
  border-radius: var(--radius-full);
  background: var(--muted);
  color: var(--muted-foreground);
}

.lf-predict-search input {
  flex: 1 1 auto;
  min-width: 0;
  border: none;
  background: transparent;
  color: var(--foreground);
  font-size: 0.8rem;
  outline: none;
}

/* ── the model tree ─────────────────────────────────────────────────────── */
.lf-predict-tree {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.lf-predict-tree__projectName {
  color: var(--muted-foreground);
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  margin: 0 0 6px;
}

.lf-predict-tree__model + .lf-predict-tree__model {
  margin-top: 10px;
}

.lf-predict-tree__modelHead {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 6px;
  margin-bottom: 4px;
}

.lf-predict-tree__modelName {
  color: var(--foreground);
  font-size: 0.83rem;
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.lf-predict-tree__versions {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.lf-predict-version {
  width: 100%;
  text-align: left;
  background: var(--muted);
  border: 2px solid transparent;
  border-radius: var(--radius);
  padding: 8px 10px;
  cursor: pointer;
  transition: all var(--transition);
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.lf-predict-version:hover:not(:disabled) {
  border-color: var(--primary);
  background: var(--card-hover);
}

.lf-predict-version.is-selected {
  border-color: var(--primary);
  background: var(--primary-soft);
}

.lf-predict-version.is-unavailable {
  cursor: not-allowed;
  opacity: 0.65;
}

.lf-predict-version__row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 6px;
}

.lf-predict-version__name {
  color: var(--foreground);
  font-size: 0.8rem;
  font-weight: 600;
}

.lf-predict-version__count {
  color: var(--muted-foreground);
  font-size: 0.7rem;
  flex: 0 0 auto;
}

.lf-predict-version__reason {
  color: var(--destructive);
  font-size: 0.7rem;
  line-height: 1.35;
}

.lf-predict-version__classes {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}

.lf-predict-chip {
  background: var(--secondary);
  color: var(--secondary-foreground);
  padding: 1px 7px;
  border-radius: var(--radius-full);
  font-size: 0.68rem;
  display: inline-flex;
  align-items: center;
  gap: 5px;
}

.lf-predict-chip--more {
  background: var(--secondary-hover);
  color: var(--foreground);
}

.lf-predict-chip--count {
  font-size: 0.74rem;
  padding: 3px 9px;
}

.lf-predict-swatch {
  width: 9px;
  height: 9px;
  border-radius: 2px;
  display: inline-block;
  flex: 0 0 auto;
}

/* ── drop zone ──────────────────────────────────────────────────────────── */
.lf-predict-drop {
  flex: 1 1 auto;
  min-height: 190px;
  border: 2px dashed var(--border-strong);
  border-radius: var(--radius);
  background: var(--muted);
  color: var(--muted-foreground);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  text-align: center;
  cursor: pointer;
  transition: all var(--transition);
  padding: 20px;
}

.lf-predict-drop:hover {
  border-color: var(--primary);
  background: var(--primary-soft);
}

/* The whole body is the drop target, so the drag feedback lives there too —
   otherwise dragging over a panel that already holds images shows nothing and
   reads as "you cannot drop here". */
.lf-predict-panel__body.is-dragging {
  outline: 2px dashed var(--primary);
  outline-offset: -6px;
  background: var(--primary-soft);
}

.lf-predict-drop i {
  font-size: 2.2rem;
  opacity: 0.75;
}

.lf-predict-drop__title {
  color: var(--foreground);
  font-weight: 600;
  font-size: 0.88rem;
  margin: 0;
}

.lf-predict-drop__hint {
  font-size: 0.76rem;
  margin: 0;
}

.lf-predict-file {
  display: none;
}

/* ── the image and its overlay ──────────────────────────────────────────── */
.lf-predict-canvas {
  position: relative;
  display: block;
  /* Shrink-wrap the image so the SVG overlay covers exactly its rendered box —
     but never wider than the column. Both halves are needed: without
     fit-content the overlay is stretched across empty panel; without the
     max-width a big photo widens the whole grid track. */
  width: fit-content;
  max-width: 100%;
  margin: 0 auto;
  border-radius: var(--radius-sm);
  overflow: hidden;
  background: var(--muted);
}

.lf-predict-canvas__img {
  display: block;
  max-width: 100%;
  max-height: 46vh;
  width: auto;
  height: auto;
}

/* The SVG covers exactly the rendered image box and carries the image's own
   natural size as its viewBox, so the browser does the scaling and the boxes
   track the image through every resize with no JS. \`pointer-events: none\` on
   the layer, re-enabled per detection group, so hovering the picture between
   boxes does not steal the cursor. */
.lf-predict-canvas__overlay {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
}

.lf-predict-inline-error {
  color: var(--destructive);
  font-size: 0.78rem;
  margin: 0;
  display: flex;
  align-items: flex-start;
  gap: 6px;
}

/* ── the thumbnail strip ────────────────────────────────────────────────── */
.lf-predict-strip {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.lf-predict-thumb {
  position: relative;
  width: 58px;
  height: 58px;
  border-radius: var(--radius-sm);
  overflow: hidden;
  border: 2px solid var(--border);
  background: var(--muted);
  flex: 0 0 auto;
}

.lf-predict-thumb.is-active {
  border-color: var(--primary);
}

.lf-predict-thumb__btn {
  width: 100%;
  height: 100%;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  display: block;
  color: var(--muted-foreground);
}

.lf-predict-thumb__btn img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.lf-predict-thumb__status {
  position: absolute;
  left: 2px;
  bottom: 2px;
  font-size: 0.6rem;
  line-height: 1;
  padding: 2px 3px;
  border-radius: var(--radius-sm);
  background: var(--card);
}

.lf-predict-thumb__status.is-done {
  color: var(--success);
}

.lf-predict-thumb__status.is-failed {
  color: var(--destructive);
}

.lf-predict-thumb__status.is-running {
  color: var(--primary);
}

.lf-predict-thumb__status.is-pending {
  color: var(--muted-foreground);
}

.lf-predict-thumb__remove {
  position: absolute;
  top: 2px;
  right: 2px;
  width: 16px;
  height: 16px;
  border: none;
  border-radius: var(--radius-full);
  background: var(--card);
  color: var(--muted-foreground);
  font-size: 0.58rem;
  line-height: 1;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
}

.lf-predict-thumb__remove:hover:not(:disabled) {
  color: var(--destructive);
}

.lf-predict-thumb--add {
  border-style: dashed;
  color: var(--muted-foreground);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}

.lf-predict-thumb--add:hover:not(:disabled) {
  border-color: var(--primary);
  color: var(--primary);
}

.lf-predict-rejected {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 3px;
  color: var(--warning);
  font-size: 0.74rem;
}

/* ── controls ───────────────────────────────────────────────────────────── */
.lf-predict-controls {
  flex: 0 0 auto;
  border-top: 1px solid var(--border);
  padding: 10px 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.lf-predict-slider {
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.lf-predict-slider__label {
  color: var(--muted-foreground);
  font-size: 0.74rem;
}

.lf-predict-slider__label strong {
  color: var(--foreground);
}

.lf-predict-slider input[type='range'] {
  width: 100%;
  accent-color: var(--primary);
}

.lf-predict-controls__actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.lf-predict-progress {
  color: var(--muted-foreground);
  font-size: 0.74rem;
  margin-right: auto;
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.lf-predict-progress__ms {
  color: var(--muted-foreground);
}

/* ── results ────────────────────────────────────────────────────────────── */
.lf-predict-subhead {
  color: var(--muted-foreground);
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin: 0 0 6px;
}

.lf-predict-top {
  text-align: center;
  padding: 12px;
  background: var(--primary-soft);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.lf-predict-top__label {
  color: var(--muted-foreground);
  font-size: 0.68rem;
  text-transform: uppercase;
  letter-spacing: 1px;
}

.lf-predict-top__class {
  color: var(--foreground);
  font-size: 1.25rem;
  font-weight: 700;
  word-break: break-word;
}

.lf-predict-top__conf {
  color: var(--success);
  font-size: 0.8rem;
  font-weight: 600;
}

.lf-predict-bars {
  display: flex;
  flex-direction: column;
  gap: 7px;
}

.lf-predict-bar__head {
  display: flex;
  justify-content: space-between;
  gap: 8px;
  color: var(--foreground);
  font-size: 0.76rem;
  margin-bottom: 3px;
}

.lf-predict-bar__track {
  height: 6px;
  background: var(--secondary);
  border-radius: var(--radius-full);
  overflow: hidden;
}

.lf-predict-bar__fill {
  height: 100%;
  border-radius: var(--radius-full);
  transition: width 0.25s ease;
}

.lf-predict-summary__chips {
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
}

.lf-predict-detections {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.lf-predict-detection {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 5px 8px;
  border: 1px solid transparent;
  border-radius: var(--radius-sm);
  background: var(--muted);
  cursor: default;
  text-align: left;
}

.lf-predict-detection.is-highlighted {
  border-color: var(--primary);
  background: var(--primary-soft);
}

.lf-predict-detection__name {
  color: var(--foreground);
  font-size: 0.78rem;
  flex: 1 1 auto;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.lf-predict-detection__conf {
  color: var(--muted-foreground);
  font-size: 0.74rem;
  font-weight: 600;
  flex: 0 0 auto;
}

.lf-predict-meta {
  margin-top: auto;
  padding-top: 10px;
  border-top: 1px solid var(--border);
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.lf-predict-meta__row {
  display: flex;
  justify-content: space-between;
  gap: 10px;
  font-size: 0.72rem;
  color: var(--muted-foreground);
}

.lf-predict-meta__row strong {
  color: var(--foreground);
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 60%;
}

.lf-sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

/* ── narrow ─────────────────────────────────────────────────────────────── */
@media (max-width: 1200px) {
  .lf-predict__grid {
    grid-template-columns: minmax(220px, 1fr) minmax(300px, 1.6fr);
    grid-template-rows: minmax(0, 1fr) auto;
  }

  .lf-predict-panel--right {
    grid-column: 1 / -1;
  }
}

@media (max-width: 992px) {
  .lf-predict__grid {
    /* Stacked, and the page scrolls — \`.lf-fill-viewport\` drops its fixed
       height at this breakpoint too, so a row that fills would collapse each
       panel to a third of a phone screen. */
    grid-template-columns: 1fr;
    grid-template-rows: none;
  }

  .lf-predict-panel {
    max-height: 70vh;
  }

  .lf-predict-panel--right {
    grid-column: auto;
  }
}
`, "",{"version":3,"sources":["webpack://./src/pages/prediction/styles/prediction.css"],"names":[],"mappings":"AAAA;;;;;;;+EAO+E;;AAE/E;EACE,aAAa;EACb,sBAAsB;EACtB,SAAS;EACT,UAAU;EACV;;qBAEmB;AACrB;;AAEA;EACE,cAAc;EACd,kBAAkB;AACpB;;AAEA;EACE,wBAAwB;EACxB,kBAAkB;EAClB,gBAAgB;EAChB,SAAS;AACX;;AAEA;EACE,8BAA8B;EAC9B,kBAAkB;EAClB,eAAe;AACjB;;AAEA;EACE,cAAc;EACd,aAAa;EACb,aAAa;EACb,iFAAiF;EACjF;;;;;6EAK2E;EAC3E,kCAAkC;EAClC,SAAS;AACX;;AAEA,8EAA8E;AAC9E;EACE,aAAa;EACb,sBAAsB;EACtB,aAAa;EACb;;;;qCAImC;EACnC,YAAY;EACZ,uBAAuB;EACvB,+BAA+B;EAC/B,+BAA+B;EAC/B,4BAA4B;EAC5B,gBAAgB;AAClB;;AAEA;EACE,cAAc;EACd,aAAa;EACb,mBAAmB;EACnB,8BAA8B;EAC9B,QAAQ;EACR,kBAAkB;EAClB,sCAAsC;AACxC;;AAEA;EACE,wBAAwB;EACxB,kBAAkB;EAClB,gBAAgB;EAChB,SAAS;EACT,gBAAgB;EAChB,uBAAuB;EACvB,mBAAmB;AACrB;;AAEA;EACE,8BAA8B;EAC9B,kBAAkB;EAClB,yBAAyB;EACzB,qBAAqB;EACrB,cAAc;AAChB;;AAEA;EACE,cAAc;EACd,aAAa;EACb,gBAAgB;EAChB,aAAa;EACb,aAAa;EACb,sBAAsB;EACtB,SAAS;AACX;;AAEA;EACE,uBAAuB;EACvB,+BAA+B;EAC/B,+BAA+B;EAC/B,8BAA8B;EAC9B,WAAW;EACX,YAAY;EACZ,oBAAoB;EACpB,mBAAmB;EACnB,uBAAuB;EACvB,eAAe;EACf,iCAAiC;AACnC;;AAEA;EACE,wBAAwB;EACxB,kCAAkC;EAClC,wBAAwB;AAC1B;;AAEA,8EAA8E;AAC9E;EACE,cAAc;EACd,aAAa;EACb,sBAAsB;EACtB,mBAAmB;EACnB,uBAAuB;EACvB,kBAAkB;EAClB,QAAQ;EACR,kBAAkB;EAClB,8BAA8B;AAChC;;AAEA;EACE,eAAe;EACf,YAAY;AACd;;AAEA;EACE,wBAAwB;EACxB,kBAAkB;EAClB,gBAAgB;EAChB,SAAS;AACX;;AAEA;EACE,kBAAkB;EAClB,SAAS;EACT,eAAe;AACjB;;AAEA,8EAA8E;AAC9E;EACE,cAAc;EACd,aAAa;EACb,mBAAmB;EACnB,QAAQ;EACR,mBAAmB;EACnB,iBAAiB;EACjB,+BAA+B;EAC/B,iCAAiC;EACjC,wBAAwB;EACxB,8BAA8B;AAChC;;AAEA;EACE,cAAc;EACd,YAAY;EACZ,YAAY;EACZ,uBAAuB;EACvB,wBAAwB;EACxB,iBAAiB;EACjB,aAAa;AACf;;AAEA,8EAA8E;AAC9E;EACE,aAAa;EACb,sBAAsB;EACtB,SAAS;AACX;;AAEA;EACE,8BAA8B;EAC9B,iBAAiB;EACjB,gBAAgB;EAChB,yBAAyB;EACzB,qBAAqB;EACrB,eAAe;AACjB;;AAEA;EACE,gBAAgB;AAClB;;AAEA;EACE,aAAa;EACb,mBAAmB;EACnB,8BAA8B;EAC9B,QAAQ;EACR,kBAAkB;AACpB;;AAEA;EACE,wBAAwB;EACxB,kBAAkB;EAClB,gBAAgB;EAChB,gBAAgB;EAChB,uBAAuB;EACvB,mBAAmB;AACrB;;AAEA;EACE,gBAAgB;EAChB,SAAS;EACT,UAAU;EACV,aAAa;EACb,sBAAsB;EACtB,QAAQ;AACV;;AAEA;EACE,WAAW;EACX,gBAAgB;EAChB,wBAAwB;EACxB,6BAA6B;EAC7B,4BAA4B;EAC5B,iBAAiB;EACjB,eAAe;EACf,iCAAiC;EACjC,aAAa;EACb,sBAAsB;EACtB,QAAQ;AACV;;AAEA;EACE,4BAA4B;EAC5B,6BAA6B;AAC/B;;AAEA;EACE,4BAA4B;EAC5B,+BAA+B;AACjC;;AAEA;EACE,mBAAmB;EACnB,aAAa;AACf;;AAEA;EACE,aAAa;EACb,mBAAmB;EACnB,8BAA8B;EAC9B,QAAQ;AACV;;AAEA;EACE,wBAAwB;EACxB,iBAAiB;EACjB,gBAAgB;AAClB;;AAEA;EACE,8BAA8B;EAC9B,iBAAiB;EACjB,cAAc;AAChB;;AAEA;EACE,yBAAyB;EACzB,iBAAiB;EACjB,iBAAiB;AACnB;;AAEA;EACE,aAAa;EACb,eAAe;EACf,QAAQ;AACV;;AAEA;EACE,4BAA4B;EAC5B,kCAAkC;EAClC,gBAAgB;EAChB,iCAAiC;EACjC,kBAAkB;EAClB,oBAAoB;EACpB,mBAAmB;EACnB,QAAQ;AACV;;AAEA;EACE,kCAAkC;EAClC,wBAAwB;AAC1B;;AAEA;EACE,kBAAkB;EAClB,gBAAgB;AAClB;;AAEA;EACE,UAAU;EACV,WAAW;EACX,kBAAkB;EAClB,qBAAqB;EACrB,cAAc;AAChB;;AAEA,8EAA8E;AAC9E;EACE,cAAc;EACd,iBAAiB;EACjB,uCAAuC;EACvC,4BAA4B;EAC5B,wBAAwB;EACxB,8BAA8B;EAC9B,aAAa;EACb,sBAAsB;EACtB,mBAAmB;EACnB,uBAAuB;EACvB,QAAQ;EACR,kBAAkB;EAClB,eAAe;EACf,iCAAiC;EACjC,aAAa;AACf;;AAEA;EACE,4BAA4B;EAC5B,+BAA+B;AACjC;;AAEA;;qCAEqC;AACrC;EACE,kCAAkC;EAClC,oBAAoB;EACpB,+BAA+B;AACjC;;AAEA;EACE,iBAAiB;EACjB,aAAa;AACf;;AAEA;EACE,wBAAwB;EACxB,gBAAgB;EAChB,kBAAkB;EAClB,SAAS;AACX;;AAEA;EACE,kBAAkB;EAClB,SAAS;AACX;;AAEA;EACE,aAAa;AACf;;AAEA,8EAA8E;AAC9E;EACE,kBAAkB;EAClB,cAAc;EACd;;;yDAGuD;EACvD,kBAAkB;EAClB,eAAe;EACf,cAAc;EACd,+BAA+B;EAC/B,gBAAgB;EAChB,wBAAwB;AAC1B;;AAEA;EACE,cAAc;EACd,eAAe;EACf,gBAAgB;EAChB,WAAW;EACX,YAAY;AACd;;AAEA;;;;qCAIqC;AACrC;EACE,kBAAkB;EAClB,QAAQ;EACR,WAAW;EACX,YAAY;EACZ,oBAAoB;AACtB;;AAEA;EACE,yBAAyB;EACzB,kBAAkB;EAClB,SAAS;EACT,aAAa;EACb,uBAAuB;EACvB,QAAQ;AACV;;AAEA,8EAA8E;AAC9E;EACE,aAAa;EACb,eAAe;EACf,QAAQ;AACV;;AAEA;EACE,kBAAkB;EAClB,WAAW;EACX,YAAY;EACZ,+BAA+B;EAC/B,gBAAgB;EAChB,+BAA+B;EAC/B,wBAAwB;EACxB,cAAc;AAChB;;AAEA;EACE,4BAA4B;AAC9B;;AAEA;EACE,WAAW;EACX,YAAY;EACZ,UAAU;EACV,YAAY;EACZ,uBAAuB;EACvB,eAAe;EACf,cAAc;EACd,8BAA8B;AAChC;;AAEA;EACE,WAAW;EACX,YAAY;EACZ,iBAAiB;EACjB,cAAc;AAChB;;AAEA;EACE,kBAAkB;EAClB,SAAS;EACT,WAAW;EACX,iBAAiB;EACjB,cAAc;EACd,gBAAgB;EAChB,+BAA+B;EAC/B,uBAAuB;AACzB;;AAEA;EACE,qBAAqB;AACvB;;AAEA;EACE,yBAAyB;AAC3B;;AAEA;EACE,qBAAqB;AACvB;;AAEA;EACE,8BAA8B;AAChC;;AAEA;EACE,kBAAkB;EAClB,QAAQ;EACR,UAAU;EACV,WAAW;EACX,YAAY;EACZ,YAAY;EACZ,iCAAiC;EACjC,uBAAuB;EACvB,8BAA8B;EAC9B,kBAAkB;EAClB,cAAc;EACd,eAAe;EACf,aAAa;EACb,mBAAmB;EACnB,uBAAuB;EACvB,UAAU;AACZ;;AAEA;EACE,yBAAyB;AAC3B;;AAEA;EACE,oBAAoB;EACpB,8BAA8B;EAC9B,eAAe;EACf,aAAa;EACb,mBAAmB;EACnB,uBAAuB;AACzB;;AAEA;EACE,4BAA4B;EAC5B,qBAAqB;AACvB;;AAEA;EACE,gBAAgB;EAChB,SAAS;EACT,UAAU;EACV,aAAa;EACb,sBAAsB;EACtB,QAAQ;EACR,qBAAqB;EACrB,kBAAkB;AACpB;;AAEA,8EAA8E;AAC9E;EACE,cAAc;EACd,mCAAmC;EACnC,kBAAkB;EAClB,aAAa;EACb,sBAAsB;EACtB,QAAQ;AACV;;AAEA;EACE,aAAa;EACb,sBAAsB;EACtB,QAAQ;AACV;;AAEA;EACE,8BAA8B;EAC9B,kBAAkB;AACpB;;AAEA;EACE,wBAAwB;AAC1B;;AAEA;EACE,WAAW;EACX,4BAA4B;AAC9B;;AAEA;EACE,aAAa;EACb,mBAAmB;EACnB,QAAQ;EACR,eAAe;AACjB;;AAEA;EACE,8BAA8B;EAC9B,kBAAkB;EAClB,kBAAkB;EAClB,oBAAoB;EACpB,mBAAmB;EACnB,QAAQ;AACV;;AAEA;EACE,8BAA8B;AAChC;;AAEA,8EAA8E;AAC9E;EACE,8BAA8B;EAC9B,kBAAkB;EAClB,gBAAgB;EAChB,yBAAyB;EACzB,qBAAqB;EACrB,eAAe;AACjB;;AAEA;EACE,kBAAkB;EAClB,aAAa;EACb,+BAA+B;EAC/B,+BAA+B;EAC/B,4BAA4B;EAC5B,aAAa;EACb,sBAAsB;EACtB,QAAQ;AACV;;AAEA;EACE,8BAA8B;EAC9B,kBAAkB;EAClB,yBAAyB;EACzB,mBAAmB;AACrB;;AAEA;EACE,wBAAwB;EACxB,kBAAkB;EAClB,gBAAgB;EAChB,sBAAsB;AACxB;;AAEA;EACE,qBAAqB;EACrB,iBAAiB;EACjB,gBAAgB;AAClB;;AAEA;EACE,aAAa;EACb,sBAAsB;EACtB,QAAQ;AACV;;AAEA;EACE,aAAa;EACb,8BAA8B;EAC9B,QAAQ;EACR,wBAAwB;EACxB,kBAAkB;EAClB,kBAAkB;AACpB;;AAEA;EACE,WAAW;EACX,4BAA4B;EAC5B,iCAAiC;EACjC,gBAAgB;AAClB;;AAEA;EACE,YAAY;EACZ,iCAAiC;EACjC,4BAA4B;AAC9B;;AAEA;EACE,aAAa;EACb,eAAe;EACf,QAAQ;AACV;;AAEA;EACE,gBAAgB;EAChB,SAAS;EACT,UAAU;EACV,aAAa;EACb,sBAAsB;EACtB,QAAQ;AACV;;AAEA;EACE,WAAW;EACX,aAAa;EACb,mBAAmB;EACnB,QAAQ;EACR,gBAAgB;EAChB,6BAA6B;EAC7B,+BAA+B;EAC/B,wBAAwB;EACxB,eAAe;EACf,gBAAgB;AAClB;;AAEA;EACE,4BAA4B;EAC5B,+BAA+B;AACjC;;AAEA;EACE,wBAAwB;EACxB,kBAAkB;EAClB,cAAc;EACd,gBAAgB;EAChB,uBAAuB;EACvB,mBAAmB;AACrB;;AAEA;EACE,8BAA8B;EAC9B,kBAAkB;EAClB,gBAAgB;EAChB,cAAc;AAChB;;AAEA;EACE,gBAAgB;EAChB,iBAAiB;EACjB,mCAAmC;EACnC,aAAa;EACb,sBAAsB;EACtB,QAAQ;AACV;;AAEA;EACE,aAAa;EACb,8BAA8B;EAC9B,SAAS;EACT,kBAAkB;EAClB,8BAA8B;AAChC;;AAEA;EACE,wBAAwB;EACxB,gBAAgB;EAChB,gBAAgB;EAChB,uBAAuB;EACvB,mBAAmB;EACnB,cAAc;AAChB;;AAEA;EACE,kBAAkB;EAClB,UAAU;EACV,WAAW;EACX,UAAU;EACV,YAAY;EACZ,gBAAgB;EAChB,sBAAsB;EACtB,mBAAmB;EACnB,SAAS;AACX;;AAEA,8EAA8E;AAC9E;EACE;IACE,8DAA8D;IAC9D,uCAAuC;EACzC;;EAEA;IACE,mBAAmB;EACrB;AACF;;AAEA;EACE;IACE;;4CAEwC;IACxC,0BAA0B;IAC1B,wBAAwB;EAC1B;;EAEA;IACE,gBAAgB;EAClB;;EAEA;IACE,iBAAiB;EACnB;AACF","sourcesContent":["/* ==========================================================================\n   Prediction — run a trained model against an image.\n\n   Every colour here is a token from src/styles/theme.css. Nothing is\n   hardcoded, because this screen has to be correct in both themes and the\n   legacy sheet's `h5 { color: white }` makes a guess invisible on a light\n   card (see DESIGN_GUIDE.md).\n   ========================================================================== */\n\n.lf-predict {\n  display: flex;\n  flex-direction: column;\n  gap: 10px;\n  padding: 0;\n  /* Height comes from the shell (.lf-fill-viewport in responsive.css). This\n     page used to subtract a guessed 100px, which left it 57px past the bottom\n     of the screen. */\n}\n\n.lf-predict__header {\n  flex: 0 0 auto;\n  padding: 4px 2px 0;\n}\n\n.lf-predict__title {\n  color: var(--foreground);\n  font-size: 1.25rem;\n  font-weight: 700;\n  margin: 0;\n}\n\n.lf-predict__subtitle {\n  color: var(--muted-foreground);\n  font-size: 0.82rem;\n  margin: 2px 0 0;\n}\n\n.lf-predict__grid {\n  flex: 1 1 auto;\n  min-height: 0;\n  display: grid;\n  grid-template-columns: minmax(240px, 1fr) minmax(320px, 1.7fr) minmax(240px, 1fr);\n  /* The ROW has to be told to fill, not just the container. A grid row defaults\n     to `auto`, which sizes to content — so the container stretched to the height\n     the shell gave it and the three panels stopped wherever their contents ran\n     out, leaving a band of bare page under them. Caught in a screenshot; no test\n     can see it. `minmax(0, 1fr)` rather than `1fr` so a long model list scrolls\n     inside its panel instead of pushing the grid taller than the viewport. */\n  grid-template-rows: minmax(0, 1fr);\n  gap: 10px;\n}\n\n/* ── panels ─────────────────────────────────────────────────────────────── */\n.lf-predict-panel {\n  display: flex;\n  flex-direction: column;\n  min-height: 0;\n  /* `1fr` is `minmax(auto, 1fr)`, and `auto` floors at MIN-CONTENT — so one wide\n     child (the image wrapper, a long model name) pushes the track past the\n     viewport and the whole page scrolls sideways. Caught in a 375px screenshot:\n     the confidence figures, the Run button and the meta values were all off the\n     right edge. No test sees this. */\n  min-width: 0;\n  background: var(--card);\n  border: 1px solid var(--border);\n  border-radius: var(--radius-lg);\n  box-shadow: var(--shadow-sm);\n  overflow: hidden;\n}\n\n.lf-predict-panel__head {\n  flex: 0 0 auto;\n  display: flex;\n  align-items: center;\n  justify-content: space-between;\n  gap: 8px;\n  padding: 10px 12px;\n  border-bottom: 1px solid var(--border);\n}\n\n.lf-predict-panel__title {\n  color: var(--foreground);\n  font-size: 0.92rem;\n  font-weight: 650;\n  margin: 0;\n  overflow: hidden;\n  text-overflow: ellipsis;\n  white-space: nowrap;\n}\n\n.lf-predict-panel__sub {\n  color: var(--muted-foreground);\n  font-size: 0.72rem;\n  text-transform: uppercase;\n  letter-spacing: 0.5px;\n  flex: 0 0 auto;\n}\n\n.lf-predict-panel__body {\n  flex: 1 1 auto;\n  min-height: 0;\n  overflow-y: auto;\n  padding: 12px;\n  display: flex;\n  flex-direction: column;\n  gap: 12px;\n}\n\n.lf-predict-icon-btn {\n  background: transparent;\n  border: 1px solid var(--border);\n  border-radius: var(--radius-sm);\n  color: var(--muted-foreground);\n  width: 26px;\n  height: 26px;\n  display: inline-flex;\n  align-items: center;\n  justify-content: center;\n  cursor: pointer;\n  transition: all var(--transition);\n}\n\n.lf-predict-icon-btn:hover {\n  color: var(--foreground);\n  border-color: var(--border-strong);\n  background: var(--muted);\n}\n\n/* ── empty states ───────────────────────────────────────────────────────── */\n.lf-predict-empty {\n  flex: 1 1 auto;\n  display: flex;\n  flex-direction: column;\n  align-items: center;\n  justify-content: center;\n  text-align: center;\n  gap: 6px;\n  padding: 24px 14px;\n  color: var(--muted-foreground);\n}\n\n.lf-predict-empty i {\n  font-size: 2rem;\n  opacity: 0.7;\n}\n\n.lf-predict-empty__title {\n  color: var(--foreground);\n  font-size: 0.88rem;\n  font-weight: 600;\n  margin: 0;\n}\n\n.lf-predict-empty__hint {\n  font-size: 0.78rem;\n  margin: 0;\n  max-width: 30ch;\n}\n\n/* ── search ─────────────────────────────────────────────────────────────── */\n.lf-predict-search {\n  flex: 0 0 auto;\n  display: flex;\n  align-items: center;\n  gap: 8px;\n  margin: 10px 12px 0;\n  padding: 6px 10px;\n  border: 1px solid var(--border);\n  border-radius: var(--radius-full);\n  background: var(--muted);\n  color: var(--muted-foreground);\n}\n\n.lf-predict-search input {\n  flex: 1 1 auto;\n  min-width: 0;\n  border: none;\n  background: transparent;\n  color: var(--foreground);\n  font-size: 0.8rem;\n  outline: none;\n}\n\n/* ── the model tree ─────────────────────────────────────────────────────── */\n.lf-predict-tree {\n  display: flex;\n  flex-direction: column;\n  gap: 14px;\n}\n\n.lf-predict-tree__projectName {\n  color: var(--muted-foreground);\n  font-size: 0.7rem;\n  font-weight: 700;\n  text-transform: uppercase;\n  letter-spacing: 0.6px;\n  margin: 0 0 6px;\n}\n\n.lf-predict-tree__model + .lf-predict-tree__model {\n  margin-top: 10px;\n}\n\n.lf-predict-tree__modelHead {\n  display: flex;\n  align-items: center;\n  justify-content: space-between;\n  gap: 6px;\n  margin-bottom: 4px;\n}\n\n.lf-predict-tree__modelName {\n  color: var(--foreground);\n  font-size: 0.83rem;\n  font-weight: 600;\n  overflow: hidden;\n  text-overflow: ellipsis;\n  white-space: nowrap;\n}\n\n.lf-predict-tree__versions {\n  list-style: none;\n  margin: 0;\n  padding: 0;\n  display: flex;\n  flex-direction: column;\n  gap: 5px;\n}\n\n.lf-predict-version {\n  width: 100%;\n  text-align: left;\n  background: var(--muted);\n  border: 2px solid transparent;\n  border-radius: var(--radius);\n  padding: 8px 10px;\n  cursor: pointer;\n  transition: all var(--transition);\n  display: flex;\n  flex-direction: column;\n  gap: 5px;\n}\n\n.lf-predict-version:hover:not(:disabled) {\n  border-color: var(--primary);\n  background: var(--card-hover);\n}\n\n.lf-predict-version.is-selected {\n  border-color: var(--primary);\n  background: var(--primary-soft);\n}\n\n.lf-predict-version.is-unavailable {\n  cursor: not-allowed;\n  opacity: 0.65;\n}\n\n.lf-predict-version__row {\n  display: flex;\n  align-items: center;\n  justify-content: space-between;\n  gap: 6px;\n}\n\n.lf-predict-version__name {\n  color: var(--foreground);\n  font-size: 0.8rem;\n  font-weight: 600;\n}\n\n.lf-predict-version__count {\n  color: var(--muted-foreground);\n  font-size: 0.7rem;\n  flex: 0 0 auto;\n}\n\n.lf-predict-version__reason {\n  color: var(--destructive);\n  font-size: 0.7rem;\n  line-height: 1.35;\n}\n\n.lf-predict-version__classes {\n  display: flex;\n  flex-wrap: wrap;\n  gap: 4px;\n}\n\n.lf-predict-chip {\n  background: var(--secondary);\n  color: var(--secondary-foreground);\n  padding: 1px 7px;\n  border-radius: var(--radius-full);\n  font-size: 0.68rem;\n  display: inline-flex;\n  align-items: center;\n  gap: 5px;\n}\n\n.lf-predict-chip--more {\n  background: var(--secondary-hover);\n  color: var(--foreground);\n}\n\n.lf-predict-chip--count {\n  font-size: 0.74rem;\n  padding: 3px 9px;\n}\n\n.lf-predict-swatch {\n  width: 9px;\n  height: 9px;\n  border-radius: 2px;\n  display: inline-block;\n  flex: 0 0 auto;\n}\n\n/* ── drop zone ──────────────────────────────────────────────────────────── */\n.lf-predict-drop {\n  flex: 1 1 auto;\n  min-height: 190px;\n  border: 2px dashed var(--border-strong);\n  border-radius: var(--radius);\n  background: var(--muted);\n  color: var(--muted-foreground);\n  display: flex;\n  flex-direction: column;\n  align-items: center;\n  justify-content: center;\n  gap: 6px;\n  text-align: center;\n  cursor: pointer;\n  transition: all var(--transition);\n  padding: 20px;\n}\n\n.lf-predict-drop:hover {\n  border-color: var(--primary);\n  background: var(--primary-soft);\n}\n\n/* The whole body is the drop target, so the drag feedback lives there too —\n   otherwise dragging over a panel that already holds images shows nothing and\n   reads as \"you cannot drop here\". */\n.lf-predict-panel__body.is-dragging {\n  outline: 2px dashed var(--primary);\n  outline-offset: -6px;\n  background: var(--primary-soft);\n}\n\n.lf-predict-drop i {\n  font-size: 2.2rem;\n  opacity: 0.75;\n}\n\n.lf-predict-drop__title {\n  color: var(--foreground);\n  font-weight: 600;\n  font-size: 0.88rem;\n  margin: 0;\n}\n\n.lf-predict-drop__hint {\n  font-size: 0.76rem;\n  margin: 0;\n}\n\n.lf-predict-file {\n  display: none;\n}\n\n/* ── the image and its overlay ──────────────────────────────────────────── */\n.lf-predict-canvas {\n  position: relative;\n  display: block;\n  /* Shrink-wrap the image so the SVG overlay covers exactly its rendered box —\n     but never wider than the column. Both halves are needed: without\n     fit-content the overlay is stretched across empty panel; without the\n     max-width a big photo widens the whole grid track. */\n  width: fit-content;\n  max-width: 100%;\n  margin: 0 auto;\n  border-radius: var(--radius-sm);\n  overflow: hidden;\n  background: var(--muted);\n}\n\n.lf-predict-canvas__img {\n  display: block;\n  max-width: 100%;\n  max-height: 46vh;\n  width: auto;\n  height: auto;\n}\n\n/* The SVG covers exactly the rendered image box and carries the image's own\n   natural size as its viewBox, so the browser does the scaling and the boxes\n   track the image through every resize with no JS. `pointer-events: none` on\n   the layer, re-enabled per detection group, so hovering the picture between\n   boxes does not steal the cursor. */\n.lf-predict-canvas__overlay {\n  position: absolute;\n  inset: 0;\n  width: 100%;\n  height: 100%;\n  pointer-events: none;\n}\n\n.lf-predict-inline-error {\n  color: var(--destructive);\n  font-size: 0.78rem;\n  margin: 0;\n  display: flex;\n  align-items: flex-start;\n  gap: 6px;\n}\n\n/* ── the thumbnail strip ────────────────────────────────────────────────── */\n.lf-predict-strip {\n  display: flex;\n  flex-wrap: wrap;\n  gap: 8px;\n}\n\n.lf-predict-thumb {\n  position: relative;\n  width: 58px;\n  height: 58px;\n  border-radius: var(--radius-sm);\n  overflow: hidden;\n  border: 2px solid var(--border);\n  background: var(--muted);\n  flex: 0 0 auto;\n}\n\n.lf-predict-thumb.is-active {\n  border-color: var(--primary);\n}\n\n.lf-predict-thumb__btn {\n  width: 100%;\n  height: 100%;\n  padding: 0;\n  border: none;\n  background: transparent;\n  cursor: pointer;\n  display: block;\n  color: var(--muted-foreground);\n}\n\n.lf-predict-thumb__btn img {\n  width: 100%;\n  height: 100%;\n  object-fit: cover;\n  display: block;\n}\n\n.lf-predict-thumb__status {\n  position: absolute;\n  left: 2px;\n  bottom: 2px;\n  font-size: 0.6rem;\n  line-height: 1;\n  padding: 2px 3px;\n  border-radius: var(--radius-sm);\n  background: var(--card);\n}\n\n.lf-predict-thumb__status.is-done {\n  color: var(--success);\n}\n\n.lf-predict-thumb__status.is-failed {\n  color: var(--destructive);\n}\n\n.lf-predict-thumb__status.is-running {\n  color: var(--primary);\n}\n\n.lf-predict-thumb__status.is-pending {\n  color: var(--muted-foreground);\n}\n\n.lf-predict-thumb__remove {\n  position: absolute;\n  top: 2px;\n  right: 2px;\n  width: 16px;\n  height: 16px;\n  border: none;\n  border-radius: var(--radius-full);\n  background: var(--card);\n  color: var(--muted-foreground);\n  font-size: 0.58rem;\n  line-height: 1;\n  cursor: pointer;\n  display: flex;\n  align-items: center;\n  justify-content: center;\n  padding: 0;\n}\n\n.lf-predict-thumb__remove:hover:not(:disabled) {\n  color: var(--destructive);\n}\n\n.lf-predict-thumb--add {\n  border-style: dashed;\n  color: var(--muted-foreground);\n  cursor: pointer;\n  display: flex;\n  align-items: center;\n  justify-content: center;\n}\n\n.lf-predict-thumb--add:hover:not(:disabled) {\n  border-color: var(--primary);\n  color: var(--primary);\n}\n\n.lf-predict-rejected {\n  list-style: none;\n  margin: 0;\n  padding: 0;\n  display: flex;\n  flex-direction: column;\n  gap: 3px;\n  color: var(--warning);\n  font-size: 0.74rem;\n}\n\n/* ── controls ───────────────────────────────────────────────────────────── */\n.lf-predict-controls {\n  flex: 0 0 auto;\n  border-top: 1px solid var(--border);\n  padding: 10px 12px;\n  display: flex;\n  flex-direction: column;\n  gap: 8px;\n}\n\n.lf-predict-slider {\n  display: flex;\n  flex-direction: column;\n  gap: 3px;\n}\n\n.lf-predict-slider__label {\n  color: var(--muted-foreground);\n  font-size: 0.74rem;\n}\n\n.lf-predict-slider__label strong {\n  color: var(--foreground);\n}\n\n.lf-predict-slider input[type='range'] {\n  width: 100%;\n  accent-color: var(--primary);\n}\n\n.lf-predict-controls__actions {\n  display: flex;\n  align-items: center;\n  gap: 8px;\n  flex-wrap: wrap;\n}\n\n.lf-predict-progress {\n  color: var(--muted-foreground);\n  font-size: 0.74rem;\n  margin-right: auto;\n  display: inline-flex;\n  align-items: center;\n  gap: 8px;\n}\n\n.lf-predict-progress__ms {\n  color: var(--muted-foreground);\n}\n\n/* ── results ────────────────────────────────────────────────────────────── */\n.lf-predict-subhead {\n  color: var(--muted-foreground);\n  font-size: 0.72rem;\n  font-weight: 700;\n  text-transform: uppercase;\n  letter-spacing: 0.5px;\n  margin: 0 0 6px;\n}\n\n.lf-predict-top {\n  text-align: center;\n  padding: 12px;\n  background: var(--primary-soft);\n  border: 1px solid var(--border);\n  border-radius: var(--radius);\n  display: flex;\n  flex-direction: column;\n  gap: 2px;\n}\n\n.lf-predict-top__label {\n  color: var(--muted-foreground);\n  font-size: 0.68rem;\n  text-transform: uppercase;\n  letter-spacing: 1px;\n}\n\n.lf-predict-top__class {\n  color: var(--foreground);\n  font-size: 1.25rem;\n  font-weight: 700;\n  word-break: break-word;\n}\n\n.lf-predict-top__conf {\n  color: var(--success);\n  font-size: 0.8rem;\n  font-weight: 600;\n}\n\n.lf-predict-bars {\n  display: flex;\n  flex-direction: column;\n  gap: 7px;\n}\n\n.lf-predict-bar__head {\n  display: flex;\n  justify-content: space-between;\n  gap: 8px;\n  color: var(--foreground);\n  font-size: 0.76rem;\n  margin-bottom: 3px;\n}\n\n.lf-predict-bar__track {\n  height: 6px;\n  background: var(--secondary);\n  border-radius: var(--radius-full);\n  overflow: hidden;\n}\n\n.lf-predict-bar__fill {\n  height: 100%;\n  border-radius: var(--radius-full);\n  transition: width 0.25s ease;\n}\n\n.lf-predict-summary__chips {\n  display: flex;\n  flex-wrap: wrap;\n  gap: 5px;\n}\n\n.lf-predict-detections {\n  list-style: none;\n  margin: 0;\n  padding: 0;\n  display: flex;\n  flex-direction: column;\n  gap: 3px;\n}\n\n.lf-predict-detection {\n  width: 100%;\n  display: flex;\n  align-items: center;\n  gap: 8px;\n  padding: 5px 8px;\n  border: 1px solid transparent;\n  border-radius: var(--radius-sm);\n  background: var(--muted);\n  cursor: default;\n  text-align: left;\n}\n\n.lf-predict-detection.is-highlighted {\n  border-color: var(--primary);\n  background: var(--primary-soft);\n}\n\n.lf-predict-detection__name {\n  color: var(--foreground);\n  font-size: 0.78rem;\n  flex: 1 1 auto;\n  overflow: hidden;\n  text-overflow: ellipsis;\n  white-space: nowrap;\n}\n\n.lf-predict-detection__conf {\n  color: var(--muted-foreground);\n  font-size: 0.74rem;\n  font-weight: 600;\n  flex: 0 0 auto;\n}\n\n.lf-predict-meta {\n  margin-top: auto;\n  padding-top: 10px;\n  border-top: 1px solid var(--border);\n  display: flex;\n  flex-direction: column;\n  gap: 3px;\n}\n\n.lf-predict-meta__row {\n  display: flex;\n  justify-content: space-between;\n  gap: 10px;\n  font-size: 0.72rem;\n  color: var(--muted-foreground);\n}\n\n.lf-predict-meta__row strong {\n  color: var(--foreground);\n  font-weight: 600;\n  overflow: hidden;\n  text-overflow: ellipsis;\n  white-space: nowrap;\n  max-width: 60%;\n}\n\n.lf-sr-only {\n  position: absolute;\n  width: 1px;\n  height: 1px;\n  padding: 0;\n  margin: -1px;\n  overflow: hidden;\n  clip: rect(0, 0, 0, 0);\n  white-space: nowrap;\n  border: 0;\n}\n\n/* ── narrow ─────────────────────────────────────────────────────────────── */\n@media (max-width: 1200px) {\n  .lf-predict__grid {\n    grid-template-columns: minmax(220px, 1fr) minmax(300px, 1.6fr);\n    grid-template-rows: minmax(0, 1fr) auto;\n  }\n\n  .lf-predict-panel--right {\n    grid-column: 1 / -1;\n  }\n}\n\n@media (max-width: 992px) {\n  .lf-predict__grid {\n    /* Stacked, and the page scrolls — `.lf-fill-viewport` drops its fixed\n       height at this breakpoint too, so a row that fills would collapse each\n       panel to a third of a phone screen. */\n    grid-template-columns: 1fr;\n    grid-template-rows: none;\n  }\n\n  .lf-predict-panel {\n    max-height: 70vh;\n  }\n\n  .lf-predict-panel--right {\n    grid-column: auto;\n  }\n}\n"],"sourceRoot":""}]);
// Exports
___CSS_LOADER_EXPORT___.locals = {};
export default ___CSS_LOADER_EXPORT___;
